<script setup lang="ts">
// Vue
import { computed } from "vue";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";

// Types
import { WizardProjectType } from "@/features/BuildingProject";

// Utils
import format from "@/services/Utils/format";

// Components
import Divider from "@/components/Reusables/Divider/Divider.vue";

const props = withDefaults(
  defineProps<{
    totalReferenceArea: number;
    project: WizardProjectType;
    /**
     * Whether at least one additional reference area row exists, by row count rather than summed value —
     * a row entered as 0 still counts as present. With none, the additional-areas and grand-total stats
     * would just repeat the first stat, so only the first renders.
     */
    hasAdditionalReferenceAreas?: boolean;
  }>(),
  { hasAdditionalReferenceAreas: true }
);

// Read-only screen: it needs the project, not a copy of it.
const { project } = useWizardProject(() => props.project);

const totalOne = computed(() => {
  const referencedAreas = project.value.emissionCalculationAssumptions.referencedAreas;
  return (referencedAreas?.areaBelowGround || 0) + (referencedAreas?.areaAboveGround || 0);
});

const totalTwo = computed(() => {
  return totalOne.value + (props.totalReferenceArea || 0);
});
</script>

<template>
  <div class="flex flex-col gap-4 md:flex-row">
    <div class="flex flex-1 flex-col gap-1">
      <span class="t-sh3 text-type-default tabular-nums">{{ format.number(totalOne) }} m&#178;</span>
      <span class="t-caption text-type-muted relative flex flex-col">
        <span>Area above ground</span>
        <span class="before:absolute before:-left-4 before:content-['+']">Area below ground</span>
      </span>
    </div>

    <template v-if="props.hasAdditionalReferenceAreas">
      <Divider
        orientation="vertical"
        class="hidden md:block" />

      <div class="flex flex-1 flex-col gap-1">
        <span class="t-sh3 text-type-default tabular-nums">{{ format.number(props.totalReferenceArea) }} m&#178;</span>
        <span class="t-caption text-type-muted">Additional reference areas</span>
      </div>

      <Divider
        orientation="vertical"
        class="hidden md:block" />

      <div class="flex flex-1 flex-col gap-1">
        <span class="t-sh3 text-type-default tabular-nums">{{ format.number(totalTwo) }} m&#178;</span>
        <span class="t-caption text-type-muted relative flex flex-col">
          <span>Total area</span>
          <span class="before:absolute before:-left-4 before:content-['+']">Additional reference areas</span>
        </span>
      </div>
    </template>
  </div>
</template>
